import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { sessionManager } from '../lib/sessionStorage';
import { checkForSensitiveData } from '../utils/security';

// Auto-link any pending accountability partner invitations to the newly signed-up user
const linkPendingPartnerInvites = async (userId, email) => {
  try {
    const { error } = await supabase
      .from('accountability_partners')
      .update({ partner_id: userId })
      .eq('partner_email', email.toLowerCase())
      .is('partner_id', null);
    if (error) console.error('Error linking partner invites:', error);
  } catch (err) {
    console.error('Error in linkPendingPartnerInvites:', err);
  }
};

const AuthContext = createContext({});

export const useAuth = () => {
  return useContext(AuthContext);
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [hasProfile, setHasProfile] = useState(null); // null = not checked, true/false
  const [userGroup, setUserGroup] = useState('free');
  const [verificationSent, setVerificationSent] = useState(false);
  const [signUpSession, setSignUpSession] = useState(null);
  const [resetSession, setResetSession] = useState(null);
  const adminFetchedRef = React.useRef(false);

  useEffect(() => {
    // Security check - warn if sensitive data is found
    if (checkForSensitiveData()) {
      console.warn('⚠️ Security Warning: Sensitive data found in localStorage. This has been cleared.');
    }

    // Check active sessions
    const checkUser = async () => {
      try {
        await sessionManager.initialize();

        // First priority: Check if we have a session in Supabase (authenticated users)
        const { data: { session }, error } = await supabase.auth.getSession();

        if (session?.user) {
          const normalizedUser = {
            ...session.user,
            email_confirmed_at: session.user.email_confirmed_at ?? session.user.emailVerified ?? null,
          };

          // Check profile existence AND admin status in a single query
          const { data: profile } = await supabase
            .from('profiles')
            .select('id, is_admin, user_group')
            .eq('id', session.user.id)
            .single();

          setHasProfile(!!profile);
          if (profile) {
            setIsAdmin(profile.is_admin === true);
            setUserGroup(profile.user_group || 'free');
            adminFetchedRef.current = true; // Skip redundant admin fetch
          }
          setUser(normalizedUser);
          sessionManager.setUser(normalizedUser);
        } else {
          // No Supabase session — clear any stale cached user.
          // Previously we trusted the sessionManager cache here, but if
          // localStorage was cleared (destroying the Supabase auth tokens),
          // the cached user has no valid session and all DB queries fail.
          sessionManager.clearSession();
          setUser(null);
          if (error) {
            console.warn('Session check error:', error);
          }
        }
      } catch (error) {
        console.warn('Session check error:', error);
        setUser(null);
        sessionManager.clearSession();
      } finally {
        setLoading(false);
      }
    };

    // Safety timeout to ensure app doesn't hang on white screen
    const timeoutId = setTimeout(() => {
      setLoading(false);
    }, 5000); // 5 second max wait for session check

    checkUser().then(() => clearTimeout(timeoutId));

    // Listen for auth state changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      try {
        if (session?.user) {
          sessionManager.setUser(session.user);
          // setUser(session.user); // Prevent auto-redirect on SIGNED_IN
        } else if (event === 'SIGNED_OUT') {
          sessionManager.clearSession();
          setUser(null);
        }
      } catch (error) {
        console.warn('Auth state change error:', error);
      }
    });

    return () => {
      clearTimeout(timeoutId);
      subscription.unsubscribe();
    };
  }, []);

  // Fetch admin status when user changes (skipped if already fetched during init)
  useEffect(() => {
    if (!user?.id || user.id === 'guest-user-123') {
      setIsAdmin(false);
      setUserGroup('free');
      adminFetchedRef.current = false;
      return;
    }
    // Skip if already fetched during initial session check
    if (adminFetchedRef.current) return;
    adminFetchedRef.current = true;
    const fetchAdmin = async () => {
      try {
        const { data, error } = await supabase
          .from('profiles')
          .select('is_admin, user_group')
          .eq('id', user.id)
          .single();
        if (error) {
          console.error('Admin check error:', error);
          setIsAdmin(false);
          setUserGroup('free');
          return;
        }
        setIsAdmin(data?.is_admin === true);
        setUserGroup(data?.user_group || 'free');
      } catch (err) {
        console.error('Admin check catch:', err);
        setIsAdmin(false);
        setUserGroup('free');
      }
    };
    fetchAdmin();
  }, [user?.id]);

  const value = {
    // Step 1: Start signup process by sending OTP
    startSignUp: async (email, fullName) => {
      try {
        const { data, error } = await supabase.auth.signInWithOtp({
          email,
          options: {
            data: fullName ? { full_name: fullName } : undefined
          }
        });
        if (error) {
          if (error.status === 429 || error.message?.includes('rate limit')) {
            throw new Error('Too many attempts. Please wait a few minutes before trying again.');
          }
          throw error;
        }
        setSignUpSession({ email, fullName });
        setVerificationSent(true);
        return data;
      } catch (error) {
        throw error;
      }
    },

    // Step 2: Verify OTP
    verifyOtp: async (email, token) => {
      try {
        const { data, error } = await supabase.auth.verifyOtp({
          email,
          token,
          type: 'email'
        });
        if (error) {
          if (error.message.includes('Token has expired or is invalid')) {
            throw new Error('Wrong OTP code. Please try again.');
          }
          throw error;
        }
        // Do NOT set user state here to prevent auto-redirect.
        // We wait for password setting to complete.
        return data;
      } catch (error) {
        throw error;
      }
    },

    // Step 3: Complete signup by setting password
    completeSignUp: async (email, password, fullName) => {
      try {
        const { data, error } = await supabase.auth.updateUser({
          password: password
        });
        if (error) throw error;

        if (data?.user) {
          // Create/update profile
          const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
          await supabase
            .from('profiles')
            .upsert({
              id: data.user.id,
              email: email,
              displayName: fullName,
              created_at: new Date().toISOString(),
              timeZone
            }, { onConflict: 'id' });

          setHasProfile(true);
          sessionManager.setUser(data.user);
          setUser(data.user);

          // Auto-link pending accountability partner invitations
          linkPendingPartnerInvites(data.user.id, email);
        }

        return data;
      } catch (error) {
        throw error;
      }
    },

    // Regular password-based login
    signIn: async (email, password) => {
      try {
        const { data, error } = await supabase.auth.signInWithPassword({
          email,
          password
        });
        if (error) throw error;

        if (data.user) {
          setHasProfile(true);
          sessionManager.setUser(data.user);
          setUser(data.user);

          // Auto-link pending accountability partner invitations on login
          linkPendingPartnerInvites(data.user.id, email);
        }

        return data;
      } catch (error) {
        throw error;
      }
    },

    // Request password reset (Using Magic Link OTP as configured by user)
    requestPasswordReset: async (email) => {
      try {
        const cleanEmail = email.trim();

        // Use signInWithOtp which sends the 'Magic Link' email template.
        // We set shouldCreateUser: false to ensure we only send to existing accounts.
        const { data, error } = await supabase.auth.signInWithOtp({
          email: cleanEmail,
          options: {
            shouldCreateUser: false
          }
        });

        if (error) {
          // If user doesn't exist and shouldCreateUser is false, Supabase might return an error
          // or just not send the email (dependent on Supabase version/settings).
          // We handle common errors here.
          if (error.status === 429 || error.message?.includes('rate limit')) {
            throw new Error('Too many attempts. Please wait a few minutes before trying again.');
          }
          if (error.message?.includes('User not found') || error.status === 400) {
            throw new Error('No account found with this email address.');
          }
          throw error;
        }

        setResetSession({ email: cleanEmail });
        setVerificationSent(true);
        return data;
      } catch (error) {
        console.error('requestPasswordReset error:', error);
        throw error;
      }
    },

    // Verify OTP for password reset (Using 'email' type for Magic Link OTP)
    verifyResetOtp: async (email, token) => {
      try {
        const { data, error } = await supabase.auth.verifyOtp({
          email,
          token,
          type: 'email'
        });
        if (error) throw error;
        // The user is now logged in via OTP. 
        // We will call updatePassword next to set their permanent password.
        return data;
      } catch (error) {
        throw error;
      }
    },

    // Update password after OTP verification
    updatePassword: async (password) => {
      try {
        const { data, error } = await supabase.auth.updateUser({ password });
        if (error) {
          const msg = error.message || '';
          throw new Error(
            (msg.length > 100 || /characters/.test(msg))
              ? 'Password must be at least 8 characters with uppercase, lowercase, number, and symbol.'
              : msg
          );
        }

        if (data.user) {
          const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

          const { error } = await supabase
            .from('profiles')
            .insert(
              {
                id: data.user.id, // == auth.uid()
                email: data.user.email,
                displayName: data.user.user_metadata.full_name,
                timeZone
              },
              {
                onConflict: 'id',
                ignoreDuplicates: true
              }
            );

          if (error) {
            console.error('Insert error:', error.message);
          }
          setHasProfile(true);
          sessionManager.setUser(data.user);
          setUser(data.user);
        }

        setResetSession(null);
        return data;
      } catch (error) {
        throw error;
      }
    },

    // Sign out
    signOut: async () => {
      try {
        console.log('[AuthContext] Initiating robust sign-out...');
        
        // 1. Clear React state
        setUser(null);
        setHasProfile(null);
        setSignUpSession(null);
        setResetSession(null);
        setVerificationSent(false);

        // 2. Clear application-managed session data
        sessionManager.clearSession();
        
        // 3. Clear ALL sessionStorage
        try {
          sessionStorage.clear();
        } catch (e) {
          console.warn('Error clearing sessionStorage:', e);
        }

        // 4. Robust localStorage cleanup for Supabase and app state
        try {
          const keysToRemove = [];
          for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            if (key && (
              key.startsWith('sb-') || // Supabase session tokens (sb-<project-id>-auth-token)
              key.includes('supabase.auth') ||
              key.startsWith('saydo_') ||
              key.includes('access_token') ||
              key.includes('refresh_token')
            )) {
              keysToRemove.push(key);
            }
          }
          keysToRemove.forEach(key => localStorage.removeItem(key));
          console.log(`[AuthContext] Cleared ${keysToRemove.length} auth-related keys from localStorage`);
        } catch (e) {
          console.warn('Error clearing localStorage auth keys:', e);
        }

        // 5. Finally, call Supabase signOut to notify the server and clear internal state
        const { error } = await supabase.auth.signOut();
        if (error && !error.message.includes('Auth session missing')) {
          console.warn('Supabase sign out warning:', error);
        }
        
        console.log('[AuthContext] Sign-out complete');
      } catch (error) {
        console.warn('Sign out process encountered an error:', error);
      }
    },

    // Resend verification code
    resendVerification: async (email) => {
      try {
        const { error } = await supabase.auth.signInWithOtp({
          email,
          options: {
            data: signUpSession?.fullName ? { full_name: signUpSession.fullName } : undefined
          }
        });
        if (error) {
          if (error.status === 429 || error.message?.includes('rate limit')) {
            throw new Error('Too many attempts. Please wait a few minutes before trying again.');
          }
          throw error;
        }
        setVerificationSent(true);
      } catch (error) {
        throw error;
      }
    },

    // Refresh session
    refreshSession: async () => {
      try {
        const { data, error } = await supabase.auth.refreshSession();
        if (error) throw error;
        if (data.session?.user) {
          sessionManager.setUser(data.session.user);
          setUser(data.session.user);
        }
        return data;
      } catch (error) {
        console.warn('Session refresh error:', error);
        return { user: null, session: null };
      }
    },

    user,
    isAdmin,
    userGroup,
    hasProfile,
    verificationSent,
    signUpSession,
    resetSession,
  };

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50">
        <div className="w-16 h-16 border-4 border-indigo-200 border-t-indigo-600 rounded-full animate-spin"></div>
        <div className="mt-4 text-gray-500 font-medium animate-pulse">Initializing GK4I...</div>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};